/**
 * SignalsOverviewChart
 * ====================
 *
 * One interactive X/Y time chart for the whole workspace — modeled on a stock
 * chart — that stays readable from 3 signals to 10,000:
 *
 *   - Small workspaces render exactly as before: one line per signal, event
 *     streams as sentiment-colored dots.
 *   - Large workspaces render NAMESPACE GROUP ENVELOPES: each key namespace
 *     (`sales.*`, `ecom.*`, …) draws as one median line + a shaded p10–p90
 *     band of its members' normalized values. Click a group (chip or line)
 *     to drill in — subgroups by metric, then individual member series.
 *   - Any specific signal can be PINNED from a searchable catalog and always
 *     draws as its own line on top of whatever view is active.
 *   - A VOLUME / DENSITY bar underneath doubles as a RANGE NAVIGATOR: drag to
 *     select a window, drag the window to pan, drag its edges to resize.
 *   - Fully zoomable: wheel zoom (anchored at the cursor), buttons, brush.
 *
 * Raw magnitudes differ wildly, so it defaults to "compare shapes" (each
 * series min-max normalised); group envelopes are normalized by definition.
 *
 * Data comes from the purpose-built /signal/overview endpoint: the BE
 * resolves the view (groups vs signals) so the payload scales with
 * O(groups × bins), never O(keys × bins).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Stack, Typography, ToggleButton, ToggleButtonGroup, Chip,
  IconButton, Collapse, LinearProgress, Tooltip, Dialog, alpha, useTheme,
  Autocomplete, TextField, Link,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BoltIcon from '@mui/icons-material/Bolt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PushPinIcon from '@mui/icons-material/PushPin';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { ResponsiveLine } from '@nivo/line';

import { fetchRequest as fetchOverview } from '../../redux/actions/signals/overview';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { colorForSignal, formatValue, formatDateUTC } from '../segment/studio/studioUtils';
import { sentimentDotColor } from '../../containers/charts/signalBinsMini';

const PLOT_LEFT = 48;
const PLOT_RIGHT = 24;
const NAV_BUCKETS = 160;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Keep the boundary neighbours so a windowed line still spans the visible edge.
const windowData = (data, a, b) => {
  const res = [];
  for (let i = 0; i < data.length; i += 1) {
    const t = +data[i].x;
    if (t >= a && t <= b) res.push(data[i]);
    else if (t < a && i + 1 < data.length && +data[i + 1].x >= a) res.push(data[i]);
    else if (t > b && i - 1 >= 0 && +data[i - 1].x <= b) res.push(data[i]);
  }
  return res;
};

// Min-max normalised {x, y, raw} points from signal-style bins.
const signalPoints = (bins, normalized) => {
  const raw = (bins || [])
    .map((b) => ({ t: b.from, v: Number(b.avgValue) }))
    .filter((p) => p.t != null && Number.isFinite(p.v));
  if (raw.length < 2) return [];
  let lo = Infinity;
  let hi = -Infinity;
  raw.forEach((p) => { if (p.v < lo) lo = p.v; if (p.v > hi) hi = p.v; });
  const span = hi - lo;
  return raw
    .map((p) => ({
      x: new Date(p.t),
      y: normalized ? (span > 0 ? (p.v - lo) / span : 0.5) : p.v,
      raw: p.v,
    }))
    .sort((a, b) => a.x - b.x);
};

// nivo renders slice tooltips inline inside the chart wrapper, so they get
// clipped by the short overflow-hidden chart box and painted under the
// navigator bar below it. Portal the tooltip to document.body at a fixed
// position that follows the cursor, flipping near the viewport edges.
const SliceTooltipPortal = ({ children }) => {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const onMove = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);
  if (!pos) return null;
  const flipX = pos.x > window.innerWidth - 280;
  const flipY = pos.y > window.innerHeight - 300;
  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        left: pos.x + (flipX ? -14 : 14),
        top: pos.y + (flipY ? -14 : 14),
        transform: `translate(${flipX ? '-100%' : '0'}, ${flipY ? '-100%' : '0'})`,
        // Above MUI Dialog (modal ~1300) so it also works in fullscreen mode.
        zIndex: (t) => t.zIndex.tooltip,
        pointerEvents: 'none',
      }}
    >
      {children}
    </Box>,
    document.body,
  );
};

const SignalsOverviewChart = ({ filterQuery = '', filtersSlot = null }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const { activeWorkspaceId } = useWorkspace();
  const [fullscreen, setFullscreen] = useState(false);

  const payload = useSelector((s) => s.signal_overview?.response || null);
  const isLoading = useSelector((s) => s.signal_overview?.isLoading);

  const [open, setOpen] = useState(true);
  const [scaleMode, setScaleMode] = useState('normalized');
  const [domain, setDomain] = useState(null); // [t0ms, t1ms] or null = full extent
  const [hidden, setHidden] = useState(() => new Set());  // series keys removed (X)
  const [focused, setFocused] = useState(() => new Set()); // series keys soloed (click) — multi
  const [path, setPath] = useState([]);       // drill-down group path, [] = root
  const [pins, setPins] = useState([]);       // explicitly overlaid signal keys
  const [chipSort, setChipSort] = useState('az'); // 'az' | 'move'

  const isGroupsView = payload?.mode === 'groups';
  // Group envelopes are cross-signal quantiles of NORMALIZED values — there
  // is no meaningful shared raw axis, so the groups view is always "shapes".
  const normalized = isGroupsView || scaleMode === 'normalized';

  const removeSeries = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    // Don't leave a removed series "soloed" (which would dim everything).
    setFocused((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev); next.delete(key); return next;
    });
  };
  // Toggle a series into/out of the solo set (multiple can be soloed at once).
  const toggleFocus = (key) => setFocused((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const drillInto = (groupId) => {
    setPath(String(groupId).split('/'));
    setHidden(new Set());
    setFocused(new Set());
  };
  const drillTo = (depth) => {
    setPath((prev) => prev.slice(0, depth));
    setHidden(new Set());
    setFocused(new Set());
  };

  const chartRef = useRef(null);
  const navRef = useRef(null);
  const dragRef = useRef(null);

  // Refetch whenever the workspace, the active grid filters, the drill path
  // or the pins change. takeLatest in the saga cancels stale requests.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    dispatch(fetchOverview({
      query: filterQuery,
      group: path.length ? path.join('/') : undefined,
      pins,
    }));
  }, [activeWorkspaceId, filterQuery, path, pins, dispatch]);

  // Everything the user can deviate from the default view: drill path, pins,
  // hidden/soloed series, zoom. Reset restores ALL of it, and a workspace
  // switch must never leak one workspace's drill/pins into another.
  const resetView = () => {
    setPath([]);
    setPins([]);
    setHidden(new Set());
    setFocused(new Set());
    setDomain(null);
  };
  useEffect(() => { resetView(); }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const series = useMemo(() => {
    if (!payload) return [];
    const out = [];
    let idx = 0;

    // Group envelopes: median line + p10/p90 band, values already 0..1.
    (payload.groups || []).forEach((g) => {
      const data = (g.bins || [])
        .map((b) => ({
          x: new Date(b.from),
          y: Number(b.median),
          raw: Number(b.median),
          p10: Number(b.p10),
          p90: Number(b.p90),
          nKeys: b.n_keys,
        }))
        .filter((p) => Number.isFinite(p.y))
        .sort((a, b) => a.x - b.x);
      if (data.length < 2) return;
      out.push({
        id: g.label,
        key: g.id,
        kind: 'group',
        count: g.count,
        color: colorForSignal(g.id, idx),
        data,
      });
      idx += 1;
    });

    // Individual signal rows (small workspace or drilled-in members).
    (payload.signals || []).forEach((r) => {
      const data = signalPoints(r.bins, normalized);
      if (!data.length) return;
      out.push({
        id: r.display_name || r.key,
        key: r.key,
        kind: 'signal',
        unit: r.unit || null,
        color: colorForSignal(r.key, idx),
        data,
      });
      idx += 1;
    });

    // Pinned signals always overlay as individual lines. In the groups view
    // the axis is normalized, so pins normalize too (tooltip keeps raw).
    (payload.pinned || []).forEach((r) => {
      const data = signalPoints(r.bins, normalized);
      if (!data.length) return;
      out.push({
        id: r.display_name || r.key,
        key: r.key,
        kind: 'pin',
        unit: r.unit || null,
        color: colorForSignal(r.key, idx),
        data,
      });
      idx += 1;
    });

    // Event streams: sentiment-colored dots over occurred_at.
    (payload.events || []).forEach((r) => {
      const raw = (r.points || [])
        // Plot events by OCCURRED time (when they happened), not recorded time.
        .map((p) => ({ t: p.occurred_at ?? p.timestamp, v: Number(p.rank), sentiment: p.sentiment }))
        .filter((p) => p.t != null && Number.isFinite(p.v));
      if (!raw.length) return;
      let lo = Infinity;
      let hi = -Infinity;
      raw.forEach((p) => { if (p.v < lo) lo = p.v; if (p.v > hi) hi = p.v; });
      const span = hi - lo;
      const data = raw
        .map((p) => ({
          x: new Date(p.t),
          y: normalized ? (span > 0 ? (p.v - lo) / span : 0.5) : p.v,
          raw: p.v,
          sentiment: p.sentiment,
        }))
        .sort((a, b) => a.x - b.x);
      out.push({
        id: r.display_name || r.stream || r.marketplace?.original_key || r.key,
        key: r.key,
        kind: 'event',
        color: colorForSignal(r.key, idx),
        data,
      });
      idx += 1;
    });
    return out;
  }, [payload, normalized]);

  // Full time extent across all series (the navigator's fixed frame).
  const fullExtent = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    series.forEach((s) => s.data.forEach((p) => {
      const t = +p.x; if (t < lo) lo = t; if (t > hi) hi = t;
    }));
    if (lo > hi) return null;
    // A single event (or all at one instant) has zero span, which breaks the
    // time scale (min === max). Pad ±1h so the dot renders centered.
    if (hi - lo < 1) return [lo - 3600000, hi + 3600000];
    return [lo, hi];
  }, [series]);

  // Reset the zoom window whenever the underlying extent changes.
  useEffect(() => { setDomain(null); }, [fullExtent && fullExtent[0], fullExtent && fullExtent[1]]);

  const lo = fullExtent ? fullExtent[0] : 0;
  const hi = fullExtent ? fullExtent[1] : 1;
  const fullSpan = Math.max(1, hi - lo);
  const minSpan = Math.max(1000, Math.round(fullSpan / 2000));
  const view = (domain && fullExtent) ? domain : fullExtent;
  const isZoomed = !!(domain && fullExtent && (domain[0] > lo + 1 || domain[1] < hi - 1));

  // Volume / density per time bucket (real record counts), drives the navigator.
  const density = useMemo(() => {
    const buckets = new Array(NAV_BUCKETS).fill(0);
    if (!fullExtent || !payload) return buckets;
    const bucketSpan = fullSpan / NAV_BUCKETS;
    // Spread a bin's count across every navigator bucket its [t0, t1) span
    // overlaps, proportionally to the overlap. Dropping the whole count as a
    // point mass at ``t0`` (the old behaviour) aliases whenever the backend
    // bin width isn't an exact multiple of the bucket width - e.g. 240 bins
    // into 160 buckets means buckets alternately swallow 2 and 1 bins,
    // rendering a bogus tall/short "ruler" pattern over uniform data.
    const addSpan = (t0, t1, w) => {
      // ``+new Date(x)`` coerces epoch numbers, ISO strings and Dates alike
      // (ISO strings would be NaN under a bare unary +).
      const a = t0 == null ? NaN : +new Date(t0);
      if (!Number.isFinite(a) || !(w > 0)) return;
      const b = t1 == null ? NaN : +new Date(t1);
      if (!Number.isFinite(b) || b <= a) {
        // Instantaneous mass (event dots, bins without a ``to``).
        const i = clamp(Math.floor((a - lo) / bucketSpan), 0, NAV_BUCKETS - 1);
        buckets[i] += w;
        return;
      }
      const f0 = (a - lo) / bucketSpan;
      const f1 = (b - lo) / bucketSpan;
      const total = f1 - f0;
      const i0 = Math.max(0, Math.floor(f0));
      const i1 = Math.min(NAV_BUCKETS - 1, Math.ceil(f1) - 1);
      for (let i = i0; i <= i1; i += 1) {
        const overlap = Math.min(i + 1, f1) - Math.max(i, f0);
        if (overlap > 0) buckets[i] += (w * overlap) / total;
      }
    };
    (payload.groups || []).forEach((g) => (g.bins || []).forEach(
      (b) => addSpan(Date.parse(b.from), Date.parse(b.to), Number(b.count) || 0),
    ));
    (payload.signals || []).forEach((r) => (r.bins || []).forEach(
      (b) => addSpan(Date.parse(b.from), Date.parse(b.to), Number(b.count) || 0),
    ));
    (payload.events || []).forEach((r) => (r.points || []).forEach(
      (p) => addSpan(p.known_at || p.timestamp, NaN, 1),
    ));
    return buckets;
  }, [payload, fullExtent, lo, fullSpan]);
  const densityMax = useMemo(() => Math.max(1, ...density), [density]);

  // Windowed series for the main chart (clipped to the zoom view).
  const windowedSeries = useMemo(() => {
    if (!view) return series;
    const [a, b] = view;
    return series
      .map((s) => ({ ...s, data: windowData(s.data, a, b) }))
      .filter((s) => s.data.length > 0);
  }, [series, view]);

  // Only the series the user hasn't toggled off are drawn on the chart.
  const visibleSeries = useMemo(
    () => windowedSeries.filter((s) => !hidden.has(s.key)),
    [windowedSeries, hidden],
  );

  const kindById = useMemo(() => {
    const m = new Map();
    series.forEach((s) => m.set(s.id, s.kind));
    return m;
  }, [series]);
  // Measurement unit per series (raw-values tooltip suffix).
  const unitById = useMemo(() => {
    const m = new Map();
    series.forEach((s) => { if (s.unit) m.set(s.id, s.unit); });
    return m;
  }, [series]);
  const eventIds = useMemo(
    () => new Set(series.filter((s) => s.kind === 'event').map((s) => s.id)),
    [series],
  );
  // The soloed series' nivo ids (their labels), to dim everything else.
  const focusedIds = useMemo(
    () => new Set(series.filter((s) => focused.has(s.key)).map((s) => s.id)),
    [focused, series],
  );

  // Shaded p10–p90 envelope behind each group's median line. Runs under the
  // lines layer; the polygon is built from the ORIGINAL datum (which carries
  // p10/p90) projected through nivo's scales.
  const BandsLayer = useMemo(() => ({ series: lineSeries, xScale, yScale }) => (
    <g>
      {(lineSeries || [])
        .filter((s) => kindById.get(s.id) === 'group')
        .map((s) => {
          const pts = (s.data || []).filter((d) => d.data
            && Number.isFinite(d.data.p10) && Number.isFinite(d.data.p90));
          if (pts.length < 2) return null;
          const top = pts.map((d) => `${xScale(d.data.x)},${yScale(d.data.p90)}`);
          const bot = pts.map((d) => `${xScale(d.data.x)},${yScale(d.data.p10)}`).reverse();
          const dPath = `M${top.join('L')}L${bot.join('L')}Z`;
          const dim = focusedIds.size > 0 && !focusedIds.has(s.id);
          return (
            <path key={s.id} d={dPath}
              fill={alpha(s.color, dim ? 0.04 : (dark ? 0.16 : 0.12))} stroke="none" />
          );
        })}
    </g>
  ), [kindById, focusedIds, dark]);

  // Custom lines layer so we can thicken soloed lines and make group medians
  // slightly heavier than individual signals. Colors come resolved from the
  // ``colors`` prop (dimmed for non-focused, transparent for event series).
  const LinesLayer = useMemo(() => ({ series: lineSeries, lineGenerator }) => (
    <g>
      {(lineSeries || []).map((s) => {
        const isFocusedLine = focusedIds.has(s.id);
        const isGroupLine = kindById.get(s.id) === 'group';
        return (
          <path
            key={s.id}
            d={lineGenerator(s.data.map((d) => d.position))}
            fill="none"
            stroke={s.color}
            strokeWidth={isFocusedLine ? 3.25 : (isGroupLine ? 2.25 : 1.75)}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
    </g>
  ), [focusedIds, kindById]);

  const EventDotsLayer = useMemo(() => ({ points }) => (
    <g>
      {(points || [])
        .filter((p) => eventIds.has(p.serieId))
        .map((p) => (
          <circle key={p.id} cx={p.x} cy={p.y} r={4}
            fill={sentimentDotColor(theme, p.data?.sentiment)}
            stroke={theme.palette.background.paper} strokeWidth={1}
            // Solo mode: dim dots that aren't in the focused set.
            opacity={focusedIds.size > 0 && !focusedIds.has(p.serieId) ? 0.12 : 1} />
        ))}
    </g>
  ), [eventIds, theme, focusedIds]);

  // ---- zoom helpers --------------------------------------------------------
  const clampWindow = (a, b) => {
    let w = clamp(b - a, minSpan, fullSpan);
    let na = a;
    let nb = a + w;
    if (na < lo) { na = lo; nb = lo + w; }
    if (nb > hi) { nb = hi; na = hi - w; }
    return [Math.max(lo, na), Math.min(hi, nb)];
  };
  const zoomBy = (factor) => {
    if (!view) return;
    const [a, b] = view;
    const c = (a + b) / 2;
    const w = clamp((b - a) * factor, minSpan, fullSpan);
    setDomain(clampWindow(c - w / 2, c + w / 2));
  };

  // Wheel zoom anchored at the cursor (native listener so we can preventDefault).
  useEffect(() => {
    const el = chartRef.current;
    if (!el || !fullExtent) return undefined;
    const onWheel = (e) => {
      if (!view) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const plotW = Math.max(1, rect.width - PLOT_LEFT - PLOT_RIGHT);
      const frac = clamp((e.clientX - rect.left - PLOT_LEFT) / plotW, 0, 1);
      const [a, b] = view;
      const spanv = b - a;
      const factor = e.deltaY < 0 ? 0.8 : 1 / 0.8;
      const newSpan = clamp(spanv * factor, minSpan, fullSpan);
      const center = a + frac * spanv;
      setDomain(clampWindow(center - frac * newSpan, center - frac * newSpan + newSpan));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [view, fullExtent]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- navigator brush (same drag model + affordances as the segment
  // creator's RangePicker: draggable window body, edge grips, dim scrims) ----
  const navTimeAt = (clientX) => {
    const rect = navRef.current.getBoundingClientRect();
    return lo + clamp((clientX - rect.left) / rect.width, 0, 1) * fullSpan;
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d || !navRef.current) return;
    const t = navTimeAt(e.clientX);
    if (d.mode === 'new') setDomain([Math.min(d.anchor, t), Math.max(d.anchor, t)]);
    else if (d.mode === 'pan') {
      const w = d.startB - d.startA;
      const dt = t - d.anchor;
      const na = clamp(d.startA + dt, lo, hi - w);
      setDomain([na, na + w]);
    } else if (d.mode === 'left') {
      setDomain([clamp(t, lo, d.startB - minSpan), d.startB]);
    } else if (d.mode === 'right') {
      setDomain([d.startA, clamp(t, d.startA + minSpan, hi)]);
    }
  };
  const endDrag = () => {
    const d = dragRef.current;
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', endDrag);
    document.body.style.userSelect = '';
    // A click (zero-width new selection) clears the zoom.
    if (d && d.mode === 'new' && view && Math.abs(view[1] - view[0]) < minSpan) setDomain(null);
  };
  const beginDrag = (mode) => (e) => {
    if (!fullExtent) return;
    e.preventDefault();
    e.stopPropagation();
    const anchor = navTimeAt(e.clientX);
    dragRef.current = { mode, startA: view[0], startB: view[1], anchor };
    if (mode === 'new') setDomain([anchor, anchor]);
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
  };
  // Clean up any lingering drag listeners on unmount.
  useEffect(() => endDrag, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- chips (ordered) + pin catalog ---------------------------------------
  // "Most active" = normalized movement (max − min of the median/value line)
  // inside the CURRENT view window, so sorting answers "what moved in the
  // range I'm looking at". Purely presentational — no fetch.
  const chipSeries = useMemo(() => {
    const nonEvents = windowedSeries.filter((s) => s.kind !== 'event');
    const events = series.filter((s) => s.kind === 'event');
    // Hidden series drop out of windowedSeries only via the `hidden` filter
    // on visibleSeries — keep them in chips so the X can restore them.
    const shownKeys = new Set(nonEvents.map((s) => s.key));
    const hiddenRows = series.filter((s) => s.kind !== 'event' && !shownKeys.has(s.key));
    const movement = (s) => {
      let mn = Infinity;
      let mx = -Infinity;
      (s.data || []).forEach((p) => { if (p.y < mn) mn = p.y; if (p.y > mx) mx = p.y; });
      return mn > mx ? 0 : mx - mn;
    };
    const rows = [...nonEvents, ...hiddenRows];
    if (chipSort === 'move') rows.sort((a, b) => movement(b) - movement(a));
    else rows.sort((a, b) => String(a.id).localeCompare(String(b.id)));
    return [...rows, ...events];
  }, [windowedSeries, series, chipSort]);

  const catalogOptions = useMemo(() => {
    const pinned = new Set(pins);
    return (payload?.catalog || [])
      .filter((c) => !pinned.has(c.key))
      .map((c) => ({
        key: c.key,
        group: c.group || 'other',
        label: c.display_name || c.key,
      }))
      .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));
  }, [payload, pins]);

  const groupCount = series.filter((s) => s.kind === 'group').length;
  const signalCount = payload?.total_signals ?? series.filter((s) => s.kind === 'signal').length;
  const eventCount = series.filter((s) => s.kind === 'event').length;
  const shownSignalCount = (payload?.signals || []).length;
  const memberTotal = payload?.member_total ?? 0;
  const isCapped = !isGroupsView && memberTotal > shownSignalCount && shownSignalCount > 0;
  const hasData = series.length > 0;
  if (!hasData && !isLoading) return null;

  const axisText = dark ? '#9aa0a6' : '#5f6368';
  const gridColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const nivoTheme = {
    background: 'transparent',
    textColor: axisText,
    fontSize: 11,
    axis: {
      domain: { line: { stroke: gridColor } },
      ticks: { line: { stroke: gridColor }, text: { fill: axisText } },
    },
    grid: { line: { stroke: gridColor, strokeDasharray: '3 4' } },
    crosshair: { line: { stroke: theme.palette.primary.main, strokeWidth: 1, strokeOpacity: 0.6 } },
    tooltip: { container: { background: 'transparent', boxShadow: 'none', padding: 0 } },
  };

  const winLeftPct = fullExtent ? ((view[0] - lo) / fullSpan) * 100 : 0;
  const winWidthPct = fullExtent ? ((view[1] - view[0]) / fullSpan) * 100 : 100;
  const primary = theme.palette.primary.main;

  const breadcrumbs = (path.length > 0 || isGroupsView) && (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
      <Link
        component="button"
        underline={path.length ? 'hover' : 'none'}
        onClick={() => path.length && drillTo(0)}
        sx={{
          fontSize: '0.72rem', fontWeight: path.length ? 500 : 700,
          color: path.length ? 'primary.main' : 'text.primary',
          cursor: path.length ? 'pointer' : 'default',
        }}
      >
        All signals
      </Link>
      {path.map((seg, i) => (
        <React.Fragment key={seg}>
          <Typography variant="caption" color="text.disabled">/</Typography>
          <Link
            component="button"
            underline={i < path.length - 1 ? 'hover' : 'none'}
            onClick={() => i < path.length - 1 && drillTo(i + 1)}
            sx={{
              fontSize: '0.72rem', fontWeight: i === path.length - 1 ? 700 : 500,
              color: i === path.length - 1 ? 'text.primary' : 'primary.main',
              cursor: i < path.length - 1 ? 'pointer' : 'default',
            }}
          >
            {seg}
          </Link>
        </React.Fragment>
      ))}
      {isGroupsView && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          · grouped view — click a group to drill in
        </Typography>
      )}
      {isCapped && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          · top {shownSignalCount} of {memberTotal} by records
        </Typography>
      )}
    </Stack>
  );

  const body = (
    <Box
      sx={{
        mb: fullscreen ? 0 : 2,
        height: fullscreen ? '100%' : 'auto',
        display: fullscreen ? 'flex' : 'block',
        flexDirection: 'column',
        border: fullscreen ? 'none' : `1px solid ${theme.palette.divider}`,
        borderRadius: fullscreen ? 0 : 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShowChartIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>Overview</Typography>
          <Typography variant="caption" color="text.secondary">
            {signalCount} signal{signalCount === 1 ? '' : 's'}
            {groupCount > 0 ? ` · ${groupCount} group${groupCount === 1 ? '' : 's'}` : ''}
            {` · ${eventCount} event stream${eventCount === 1 ? '' : 's'}`}
          </Typography>
          {isZoomed && view && (
            <Chip size="small" label={`${formatDateUTC(new Date(view[0]))} → ${formatDateUTC(new Date(view[1]))}`}
              sx={{ height: 20, fontSize: '0.66rem', bgcolor: alpha(primary, 0.1), color: primary }} />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack direction="row" alignItems="center">
            <Tooltip title="Zoom out"><span>
              <IconButton size="small" onClick={() => zoomBy(1 / 0.6)} disabled={!isZoomed && false}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Zoom in"><span>
              <IconButton size="small" onClick={() => zoomBy(0.6)}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Reset zoom"><span>
              <IconButton size="small" onClick={() => setDomain(null)} disabled={!isZoomed}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span></Tooltip>
          </Stack>
          <Tooltip title={isGroupsView
            ? 'Group envelopes compare shapes by definition — drill into a group or pin signals to see raw values'
            : ''}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={normalized ? 'normalized' : 'raw'}
              onChange={(_e, v) => v && !isGroupsView && setScaleMode(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', py: 0.2, px: 1, fontSize: '0.72rem',
                  border: `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              <ToggleButton value="normalized">Compare shapes</ToggleButton>
              <ToggleButton value="raw" disabled={isGroupsView}>Raw values</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Tooltip title={fullscreen ? 'Exit full screen' : 'Full screen'}>
            <IconButton size="small" onClick={() => setFullscreen((f) => !f)}>
              {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {/* Collapse only makes sense inline; full screen is always expanded. */}
          {!fullscreen && (
            <IconButton size="small" onClick={() => setOpen((o) => !o)}>
              <KeyboardArrowDownIcon
                sx={{ transition: 'transform 160ms', transform: open ? 'rotate(180deg)' : 'none' }}
              />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {isLoading && <LinearProgress sx={{ height: 2 }} />}

      {/* In full screen, surface the page filters so users can refine without
          leaving the expanded chart. */}
      {fullscreen && filtersSlot && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          {filtersSlot}
        </Box>
      )}

      <Collapse in={open || fullscreen} sx={fullscreen ? { flex: 1, minHeight: 0, display: 'flex', '& .MuiCollapse-wrapper': { flex: 1 }, '& .MuiCollapse-wrapperInner': { display: 'flex', flexDirection: 'column', height: '100%' } } : undefined}>
        {breadcrumbs && (
          <Box sx={{ px: 1.5, pb: 0.25 }}>{breadcrumbs}</Box>
        )}
        {/* Main price-style chart (zoom via wheel + navigator below). */}
        <Box ref={chartRef} sx={{ height: fullscreen ? 'calc(100vh - 320px)' : 108, px: 1, overflow: 'hidden' }}>
          {hasData && (
            <ResponsiveLine
              data={visibleSeries.length ? visibleSeries : [{ id: '__none__', data: [] }]}
              margin={{ top: 12, right: PLOT_RIGHT, bottom: 28, left: PLOT_LEFT }}
              xScale={fullExtent
                ? { type: 'time', format: 'native', min: new Date(view[0]), max: new Date(view[1]) }
                : { type: 'time', format: 'native' }}
              yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
              curve="monotoneX"
              colors={(s) => {
                // Event series draw as dots (transparent line). Solo mode dims
                // non-focused lines to a faint tint of their own color.
                if (eventIds.has(s.id)) return 'transparent';
                if (focused.size > 0 && !focused.has(s.key)) return alpha(s.color, dark ? 0.16 : 0.13);
                return s.color;
              }}
              lineWidth={1.75}
              enablePoints={false}
              enableGridX={false}
              useMesh
              enableSlices="x"
              animate={false}
              theme={nivoTheme}
              layers={['grid', 'markers', 'areas', BandsLayer, 'crosshair', LinesLayer, 'points', EventDotsLayer, 'slices', 'mesh']}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                format: (v) => (normalized ? '' : formatValue(v)),
              }}
              axisBottom={{
                tickSize: 0,
                tickPadding: 8,
                tickValues: 5,
                format: (v) => formatDateUTC(v),
              }}
              sliceTooltip={({ slice }) => {
                const x = slice.points[0]?.data?.x;
                return (
                  <SliceTooltipPortal>
                  <Box sx={{
                    bgcolor: dark ? 'rgba(20,22,20,0.96)' : 'rgba(255,255,255,0.98)',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5, p: 1, boxShadow: 4, minWidth: 180, maxHeight: 240, overflow: 'auto',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {x ? formatDateUTC(x, true) : ''}
                    </Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {slice.points.map((p) => {
                        const isGroupPt = kindById.get(p.serieId) === 'group';
                        return (
                          <Stack key={p.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.serieColor }} />
                              <Typography variant="caption">{p.serieId}</Typography>
                            </Stack>
                            <Typography variant="caption" fontWeight={700}>
                              {isGroupPt
                                ? `${Number(p.data.raw).toFixed(2)} (${Number(p.data.p10 ?? 0).toFixed(2)}–${Number(p.data.p90 ?? 0).toFixed(2)}) · ${p.data.nKeys ?? '?'} signals`
                                : `${formatValue(p.data.raw)}${unitById.get(p.serieId) ? ` ${unitById.get(p.serieId)}` : ''}`}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                  </SliceTooltipPortal>
                );
              }}
            />
          )}
        </Box>

        {/* Data-density navigator: drag the highlighted window (or its edges)
            to zoom; drag the body to pan. Mirrors the segment creator's brush. */}
        {hasData && fullExtent && (
          <Box sx={{ px: 1, pb: 0.5 }}>
            <Box
              ref={navRef}
              onPointerDown={beginDrag('new')}
              sx={{
                position: 'relative',
                height: 44,
                ml: `${PLOT_LEFT}px`,
                mr: `${PLOT_RIGHT}px`,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                overflow: 'hidden',
                cursor: 'crosshair',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >
              <svg width="100%" height="44" viewBox={`0 0 ${NAV_BUCKETS} 44`} preserveAspectRatio="none"
                style={{ display: 'block' }}>
                {density.map((c, i) => {
                  const h = (c / densityMax) * 40;
                  const center = lo + ((i + 0.5) / NAV_BUCKETS) * fullSpan;
                  const inWin = center >= view[0] && center <= view[1];
                  return (
                    <rect
                      key={i}
                      x={i + 0.1}
                      width={0.8}
                      y={44 - h}
                      height={Math.max(0.5, h)}
                      fill={primary}
                      fillOpacity={inWin ? 0.85 : 0.22}
                    />
                  );
                })}
              </svg>

              {/* dim scrims outside the window */}
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${winLeftPct}%`,
                bgcolor: dark ? 'rgba(10,12,10,0.62)' : 'rgba(255,255,255,0.6)', pointerEvents: 'none' }} />
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${Math.max(0, 100 - winLeftPct - winWidthPct)}%`,
                bgcolor: dark ? 'rgba(10,12,10,0.62)' : 'rgba(255,255,255,0.6)', pointerEvents: 'none' }} />

              {/* Selection band (draggable body) with a DRAG affordance */}
              <Box
                onPointerDown={beginDrag('pan')}
                sx={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${winLeftPct}%`, width: `${Math.max(0.5, winWidthPct)}%`,
                  border: `2px solid ${primary}`,
                  borderRadius: 1,
                  boxSizing: 'border-box',
                  cursor: 'grab',
                  bgcolor: alpha(primary, 0.1),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background-color 120ms ease',
                  '&:hover': { bgcolor: alpha(primary, 0.18) },
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.25}
                  sx={{
                    pointerEvents: 'none', px: 0.6, py: 0.1, borderRadius: 5,
                    bgcolor: alpha(primary, 0.9), color: theme.palette.getContrastText(primary),
                    opacity: winWidthPct > 8 ? 0.92 : 0,
                  }}>
                  <SwapHorizIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 800, lineHeight: 1 }}>
                    DRAG
                  </Typography>
                </Stack>
              </Box>

              {/* Edge handles (grip dots), left + right */}
              {[['left', winLeftPct], ['right', winLeftPct + winWidthPct]].map(([side, posPct]) => (
                <Box
                  key={side}
                  onPointerDown={beginDrag(side)}
                  sx={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${posPct}%`, width: 16, transform: 'translateX(-50%)',
                    cursor: 'ew-resize', zIndex: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '&:hover .grip': { transform: 'scaleY(1.12)', filter: 'brightness(1.1)' },
                  }}
                >
                  <Box className="grip" sx={{
                    width: 9, height: '78%', borderRadius: 2, bgcolor: primary,
                    boxShadow: theme.palette.boxShadow,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: '2px', transition: 'transform 120ms ease',
                  }}>
                    {[0, 1, 2].map((k) => (
                      <Box key={k} sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(primary) }} />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontSize: '0.66rem' }}>
              Drag the highlighted window (or its edges) to zoom · scroll on the chart or use the controls · bars show data density over time
            </Typography>
          </Box>
        )}

        {hasData && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, px: 1.5, pb: 1.25 }}>
            {/* Chip ordering: alphabetical or by movement in the visible window. */}
            {chipSeries.length > 8 && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={chipSort}
                onChange={(_e, v) => v && setChipSort(v)}
                sx={{
                  '& .MuiToggleButton-root': { py: 0, px: 0.6, border: `1px solid ${theme.palette.divider}` },
                }}
              >
                <ToggleButton value="az" title="Sort A–Z">
                  <SortByAlphaIcon sx={{ fontSize: 14 }} />
                </ToggleButton>
                <ToggleButton value="move" title="Sort by movement in the visible window">
                  <TrendingUpIcon sx={{ fontSize: 14 }} />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {chipSeries.map((s) => {
              const off = hidden.has(s.key);
              const muted = theme.palette.text.disabled;
              const isGroupChip = s.kind === 'group';
              const isPinChip = s.kind === 'pin';
              // Solo mode: this chip is in the focused set, or it's dimmed.
              const isFocused = focused.has(s.key);
              const dimByFocus = focused.size > 0 && !isFocused && !off;
              const label = isGroupChip ? `${s.id} (${s.count})` : s.id;
              const title = off
                ? `Click the X to restore ${s.id}`
                : (isGroupChip
                  ? `Open ${s.id} — ${s.count} signal${s.count === 1 ? '' : 's'}`
                  : (isPinChip
                    ? (isFocused ? `Remove ${s.id} from focus` : `Focus ${s.id} · X unpins it`)
                    : (isFocused ? `Remove ${s.id} from focus` : `Focus ${s.id} (dim the rest; click more to add)`)));
              return (
                <Chip
                  key={s.key}
                  size="small"
                  clickable
                  onClick={() => {
                    if (off) return;
                    if (isGroupChip) drillInto(s.key);
                    else toggleFocus(s.key);
                  }}
                  onDelete={isPinChip
                    ? () => setPins((prev) => prev.filter((k) => k !== s.key))
                    : () => removeSeries(s.key)}
                  icon={s.kind === 'event'
                    ? <BoltIcon sx={{ fontSize: 13 }} />
                    : (isPinChip ? <PushPinIcon sx={{ fontSize: 12 }} /> : undefined)}
                  label={label}
                  title={title}
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    cursor: off ? 'default' : 'pointer',
                    bgcolor: off
                      ? alpha(muted, 0.08)
                      : (isFocused ? alpha(s.color, 0.18)
                        : (isGroupChip ? alpha(s.color, 0.08) : 'transparent')),
                    border: `1px solid ${off ? alpha(muted, 0.4) : s.color}`,
                    fontWeight: (isFocused || isGroupChip) ? 700 : 400,
                    color: off ? muted : 'text.primary',
                    opacity: off ? 0.6 : (dimByFocus ? 0.4 : 1),
                    textDecoration: off ? 'line-through' : 'none',
                    transition: 'all 120ms ease',
                    '&:hover': { bgcolor: off ? alpha(muted, 0.16) : alpha(s.color, 0.12) },
                    '& .MuiChip-label': { px: 0.75, textDecoration: 'inherit' },
                    '& .MuiChip-icon': { color: off ? muted : s.color, ml: 0.5 },
                    // The X delete affordance.
                    '& .MuiChip-deleteIcon': {
                      fontSize: 15,
                      color: off ? muted : alpha(s.color, 0.7),
                      '&:hover': { color: theme.palette.error.main },
                    },
                  }}
                />
              );
            })}
            {(hidden.size > 0 || focused.size > 0 || pins.length > 0 || path.length > 0 || isZoomed) && (
              <Chip
                size="small"
                clickable
                variant="outlined"
                label={hidden.size > 0 ? `Reset (${hidden.size} hidden)` : 'Reset'}
                title="Back to the default view: top level, no pins, no hidden or focused series, full time range"
                onClick={resetView}
                sx={{ height: 20, fontSize: '0.66rem', cursor: 'pointer' }}
              />
            )}
            {/* Searchable pin catalog: overlay any signal on the current view. */}
            {catalogOptions.length > 0 && (
              <Autocomplete
                size="small"
                options={catalogOptions}
                groupBy={(o) => o.group}
                getOptionLabel={(o) => o.label}
                value={null}
                blurOnSelect
                clearOnBlur
                onChange={(_e, opt) => {
                  if (opt?.key) setPins((prev) => (prev.includes(opt.key) ? prev : [...prev, opt.key]));
                }}
                sx={{ width: 220 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add signal…"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': { height: 26, fontSize: '0.72rem', py: 0 },
                      '& .MuiOutlinedInput-input': { py: '2px !important' },
                    }}
                  />
                )}
              />
            )}
          </Stack>
        )}
      </Collapse>
    </Box>
  );

  if (fullscreen) {
    return (
      <Dialog
        fullScreen
        open
        onClose={() => setFullscreen(false)}
        PaperProps={{ sx: { bgcolor: 'background.default', p: 1.5, display: 'flex', flexDirection: 'column' } }}
      >
        {body}
      </Dialog>
    );
  }
  return body;
};

// Memoized: the parent (SignalList) re-renders on every selection toggle /
// dialog open; this chart only needs to re-render when the filter query or
// the (memoized) filters slot actually change.
export default React.memo(SignalsOverviewChart);
